import { useState } from "react";
import { api, getUser } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { KundeSuche } from "@/components/KundeSuche";
import { OHNE_LABEL_ROLES } from "@/components/OhneLabelOption";
import { toast } from "sonner";
import { AlertTriangle, Search, UserRound, X } from "lucide-react";

// „Bestehenden Kunden wählen“ beim manuellen Versand: belegt die Empfängerfelder vor (bleiben editierbar)
export function VersandKundeWahl({ sel, onChange, onEmpfaenger }) {
  const [busy, setBusy] = useState(false);
  const canSave = OHNE_LABEL_ROLES.includes(getUser()?.rolle);
  const load = async (kundeId, adresse = "auto") => {
    setBusy(true);
    try {
      const r = await api.get(`/versand/kunde-empfaenger/${kundeId}`, { params: { adresse } });
      onEmpfaenger(r.data.empfaenger);
      onChange({ ...r.data, adresse_speichern: false });
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunde konnte nicht geladen werden."); }
    finally { setBusy(false); }
  };
  if (!sel) {
    return (
      <div className="p-3 rounded-md border-2 border-blue-200 bg-blue-50/70 space-y-1.5" data-testid="versand-kunde-wahl">
        <Label htmlFor="versand-kunde-suche-input" className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
          <Search className="w-4 h-4 text-blue-700" /> Bestehenden Kunden wählen
        </Label>
        <KundeSuche onSelect={(k) => load(k.id)} testId="versand-kunde-suche" inputId="versand-kunde-suche-input" placeholder="Kunde suchen: Name, Firma, Kundennr., E-Mail, Ort" />
        <p className="text-xs text-slate-600">Übernimmt Name und Adresse des Kunden in die Empfängerfelder – oder den Empfänger unten frei eingeben.</p>
      </div>
    );
  }
  const k = sel.kunde;
  return (
    <div className="p-3 border rounded-md bg-white space-y-2" data-testid="versand-kunde-gewaehlt">
      <div className="flex items-center gap-2">
        <UserRound className="w-4 h-4 text-slate-500" />
        <div className="text-sm flex-1 min-w-0"><span className="font-medium text-slate-900">{k.firma || k.name}</span>{k.firma && k.name ? ` · ${k.name}` : ""} <span className="font-mono text-xs text-slate-500">{k.kundennummer}</span></div>
        <Button type="button" size="sm" variant="ghost" onClick={() => onChange(null, true)} disabled={busy} data-testid="versand-kunde-entfernen"><X className="w-4 h-4 mr-1" /> Auswahl entfernen</Button>
      </div>
      {sel.hat_lieferadresse && sel.hat_rechnungsadresse && (
        <div className="inline-flex rounded-md border border-slate-200 p-0.5" role="group" aria-label="Adresse wählen" data-testid="versand-kunde-adresse-toggle">
          {[["liefer", "Lieferadresse"], ["rechnung", "Rechnungsadresse"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => sel.quelle !== v && load(k.id, v)} disabled={busy} aria-pressed={sel.quelle === v}
              className={`px-3 py-1 text-xs rounded transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-slate-400 ${sel.quelle === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
              data-testid={`versand-kunde-adresse-${v}`}>{l}</button>
          ))}
        </div>
      )}
      {!sel.quelle && (
        <div className="space-y-2">
          <p className="text-sm text-amber-800 flex items-start gap-1.5" data-testid="versand-kunde-keine-adresse"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> Für diesen Kunden ist keine Adresse hinterlegt. Bitte unten eingeben.</p>
          {canSave && (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={!!sel.adresse_speichern} onCheckedChange={(v) => onChange({ ...sel, adresse_speichern: !!v })} data-testid="versand-kunde-adresse-speichern" />
              Adresse beim Kunden speichern (nach erfolgreicher Buchung)
            </label>
          )}
        </div>
      )}
    </div>
  );
}
